import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowLeftRight, Banknote, ChevronDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { getCategories, getExcludedTransfers, getTransactions, importRevolutCsv } from "@/lib/api";
import { useMediaQuery } from "@/hooks/use-media-query";
import { AnalyzingOverlay } from "@/components/AnalyzingOverlay";
import { CashEntryModal } from "@/components/CashEntryModal";
import { TransactionDetailModal } from "@/components/TransactionDetailModal";
import { categoryColor, formatSigned, formatTime, relativeDay } from "@/lib/format";

const DESK_COLS = "grid grid-cols-[minmax(0,1fr)_150px_70px_120px] items-center gap-4";

export default function Transactions() {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [txs, setTxs] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedTx, setSelectedTx] = useState(null);
  const [importing, setImporting] = useState(false);
  const [excluded, setExcluded] = useState([]);
  const [showExcluded, setShowExcluded] = useState(false);
  const [cashOpen, setCashOpen] = useState(false);
  const [scopeFilter, setScopeFilter] = useState("all");
  const fileRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    const [t, c, ex] = await Promise.all([getTransactions(), getCategories(), getExcludedTransfers()]);
    setTxs(t);
    setCategories(c);
    setExcluded(ex);
  };

  useEffect(() => {
    load().catch(() => toast.error("Errore nel caricamento delle transazioni"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apertura rapida del modale contanti via ?add=cash (dal menu dell'header).
  useEffect(() => {
    if (searchParams.get("add") === "cash") {
      setCashOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("add");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const visibleTxs = useMemo(() => {
    if (!txs) return null;
    if (scopeFilter === "all") return txs;
    return txs.filter((t) => (t.scope || "personal") === scopeFilter);
  }, [txs, scopeFilter]);

  const groups = useMemo(() => {
    if (!visibleTxs) return [];
    const map = new Map();
    for (const t of visibleTxs) {
      const label = relativeDay(t.date);
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(t);
    }
    return Array.from(map.entries());
  }, [visibleTxs]);

  const totals = useMemo(() => {
    if (!visibleTxs) return { count: 0, income: 0, expense: 0 };
    let income = 0;
    let expense = 0;
    for (const t of visibleTxs) {
      if (t.amount > 0) income += t.amount;
      else expense += t.amount;
    }
    return { count: visibleTxs.length, income, expense };
  }, [visibleTxs]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Seleziona un file .csv esportato da Revolut");
      return;
    }
    setImporting(true);
    try {
      const res = await importRevolutCsv(file);
      if (res.imported === 0) {
        toast("Nessuna transazione nuova: erano tutte già presenti");
      } else {
        toast.success(
          `${res.imported} transazioni importate · ${res.duplicates} duplicate ignorate · ${res.cards_generated} nuovi insight`
        );
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import fallito: controlla il file");
    } finally {
      setImporting(false);
    }
  };

  // Marcato/ripristinato un giroconto: ricarica liste (la pipeline gira in background).
  const handleTransferred = async () => {
    try {
      await load();
    } catch (e) {
      console.warn("[Liqo] ricarica transazioni dopo giroconto fallita:", e);
    }
  };

  // ---- Filtro Business/Personale (riusato su mobile e desktop) ----
  const SCOPE_TABS = [
    { id: "all", label: "Tutte" },
    { id: "business", label: "Business" },
    { id: "personal", label: "Personale" },
  ];
  const ScopeFilter = () => (
    <div
      className="inline-flex items-center gap-1 rounded-[8px] border border-white/[0.08] bg-[#0D0D14] p-1"
      data-testid="scope-filter"
      role="tablist"
      aria-label="Filtra per tipo di spesa"
    >
      {SCOPE_TABS.map((t) => {
        const active = scopeFilter === t.id;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={active}
            onClick={() => setScopeFilter(t.id)}
            data-testid={`scope-filter-${t.id}`}
            className={`mono rounded-[6px] px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.14em] transition-colors duration-150 ${
              active
                ? t.id === "business"
                  ? "bg-[#F5A623]/[0.12] text-[#F5A623]"
                  : "bg-white/[0.08] text-white/90"
                : "bg-transparent text-white/40 hover:text-white/70"
            }`}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );

  // ---- Sezione giroconti esclusi (riusata su mobile e desktop) ----
  const ExcludedSection = ({ compact = false }) =>
    excluded.length > 0 ? (
      <div className={compact ? "" : "mt-6"}>
        <button
          onClick={() => setShowExcluded((v) => !v)}
          data-testid="toggle-excluded-transfers-button"
          className="flex w-full items-center justify-between rounded-[8px] border border-white/[0.06] bg-[#0D0D14] px-4 py-3 text-left hover:bg-white/[0.03]"
        >
          <span className="flex items-center gap-2">
            <ArrowLeftRight size={13} className="text-white/45" />
            <span className="caps-tag !text-[10px] text-white/55">Giroconti esclusi · {excluded.length}</span>
          </span>
          <ChevronDown
            size={15}
            className={`text-white/40 transition-transform duration-200 ${showExcluded ? "rotate-180" : ""}`}
          />
        </button>

        {showExcluded && (
          <div className="mt-2 overflow-hidden rounded-[12px] border border-white/[0.06] bg-[#0D0D14]/60">
            <p className="px-4 pt-3 text-[11px] font-light leading-4 text-white/30">
              Trasferimenti tra i tuoi conti: non incidono su spese, entrate e insight. Toccane uno per ripristinarlo.
            </p>
            {excluded.map((t, i) => (
              <button
                key={t.id}
                onClick={() => setSelectedTx(t)}
                data-testid="excluded-transfer-row"
                className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left opacity-70 hover:bg-white/[0.03] ${
                  i === 0 ? "mt-2 border-t border-white/[0.04]" : "border-t border-white/[0.04]"
                }`}
              >
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-light text-white/70 line-through decoration-white/20">
                    {t.description}
                  </p>
                  <p className="caps-tag mt-1" style={{ fontSize: "9px" }}>
                    <span className="mono text-white/30">{relativeDay(t.date)} · {formatTime(t.date)}</span>
                    <span className="mx-1.5 text-white/15">·</span>
                    <span className="text-white/35">{t.transfer_source === "manual" ? "Manuale" : "Auto"}</span>
                  </p>
                </div>
                <span className="mono shrink-0 text-[13px] font-light tabular text-white/45">
                  {formatSigned(t.amount)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    ) : null;

  return (
    <div data-testid="transactions-screen" className="lg:h-screen">
      {importing && (
        <AnalyzingOverlay
          messages={[
            "Leggo il file CSV…",
            "Elimino i duplicati…",
            "Il Classifier Agent categorizza i movimenti…",
            "Ricalcolo proiezioni e insight…",
          ]}
        />
      )}

      {isDesktop ? (
        /* ===================== DESKTOP ===================== */
        <div
          className="flex h-screen flex-col overflow-hidden border-x border-white/[0.05] bg-[#050508]"
          data-testid="transactions-desktop"
        >
          {/* header */}
          <div className="flex h-[58px] items-center justify-between border-b border-white/[0.05] px-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/")}
                data-testid="transactions-back-button"
                aria-label="Torna alla home"
                className="flex h-9 w-9 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/60 hover:bg-white/[0.05]"
              >
                <ArrowLeft size={16} />
              </button>
              <span className="mono text-sm font-medium tracking-[0.1em] text-[#00FF87]">
                TRANSAZIONI <span className="text-white/25">//</span> LIQO
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCashOpen(true)}
                data-testid="transactions-add-cash-button"
                className="flex h-9 items-center gap-1.5 rounded-[6px] border border-[#00FF87]/35 bg-transparent px-3 text-[10px] font-medium uppercase tracking-[0.16em] text-[#00FF87] hover:bg-[#00FF87]/[0.06] active:scale-[0.97]"
              >
                <Banknote size={12} />
                Contanti
              </button>
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="transactions-import-csv-button"
                className="flex h-9 items-center gap-2 rounded-[6px] border border-white/[0.12] bg-transparent px-3 text-[10px] font-medium uppercase tracking-[0.18em] text-white/75 hover:bg-white/[0.05]"
              >
                <Upload size={12} />
                CSV
              </button>
            </div>
          </div>

          {/* body */}
          <div className="flex-1 overflow-y-auto px-6 py-7">
            <div className="mx-auto grid max-w-[1100px] grid-cols-[minmax(0,1fr)_320px] gap-6">
              {/* MAIN: lista a colonne */}
              <div className="min-w-0">
                <div className="mb-4">
                  <ScopeFilter />
                </div>
                {txs === null ? (
                  <div className="space-y-2 pt-2" data-testid="transactions-desktop-loading">
                    {[...Array(10)].map((_, i) => (
                      <div key={i} className="flex items-center justify-between rounded-[8px] border border-white/[0.05] px-5 py-3">
                        <div className="h-3.5 w-1/3 animate-pulse bg-white/[0.06]" />
                        <div className="h-3.5 w-16 animate-pulse bg-white/[0.06]" />
                      </div>
                    ))}
                  </div>
                ) : txs.length === 0 ? (
                  <p className="mono pt-20 text-center text-sm font-light text-white/40" data-testid="transactions-empty">
                    ❯ Nessuna transazione. Importa un CSV Revolut o aggiungi contanti.
                  </p>
                ) : (
                  <>
                    {/* intestazione colonne */}
                    <div className={`${DESK_COLS} px-5 pb-2`}>
                      <span className="caps-tag text-white/30">Descrizione</span>
                      <span className="caps-tag text-white/30">Categoria</span>
                      <span className="caps-tag text-white/30">Ora</span>
                      <span className="caps-tag text-right text-white/30">Importo</span>
                    </div>

                    <div className="space-y-5">
                      {groups.length === 0 ? (
                        <p className="mono pt-16 text-center text-sm font-light text-white/40" data-testid="scope-filter-empty">
                          ❯ Nessun movimento {scopeFilter === "business" ? "business" : "personale"} in questo elenco.
                        </p>
                      ) : (
                        groups.map(([label, items]) => (
                        <div key={label}>
                          <p className="caps-tag pb-1.5 text-white/35">{label}</p>
                          <div className="overflow-hidden rounded-[12px] border border-white/[0.06] bg-[#0D0D14]">
                            {items.map((t, i) => (
                              <button
                                key={t.id}
                                onClick={() => setSelectedTx(t)}
                                data-testid="transaction-row"
                                className={`${DESK_COLS} w-full px-5 py-3 text-left hover:bg-white/[0.03] active:bg-white/[0.05] ${
                                  i > 0 ? "border-t border-white/[0.04]" : ""
                                }`}
                              >
                                <div className="flex min-w-0 items-center gap-2">
                                  <p className="truncate text-[13px] font-light text-white/85">{t.description}</p>
                                  {t.source === "cash" && (
                                    <span
                                      className="caps-tag shrink-0 rounded-[4px] border border-[#00FF87]/35 px-1 py-0.5 text-[#00FF87]"
                                      style={{ fontSize: "8px" }}
                                      data-testid="cash-badge"
                                    >
                                      Contanti
                                    </span>
                                  )}
                                  {t.scope === "business" && (
                                    <span
                                      className="caps-tag shrink-0 rounded-[4px] border border-[#F5A623]/40 px-1 py-0.5 text-[#F5A623]"
                                      style={{ fontSize: "8px" }}
                                      data-testid="business-badge"
                                    >
                                      Business
                                    </span>
                                  )}
                                </div>
                                <span
                                  className="caps-tag truncate rounded-[4px] border px-1.5 py-0.5"
                                  style={{
                                    color: categoryColor(t.category),
                                    borderColor: `${categoryColor(t.category)}40`,
                                    fontSize: "9px",
                                  }}
                                >
                                  {t.category || "Da classificare"}
                                </span>
                                <span className="mono text-[11px] text-white/30">{formatTime(t.date)}</span>
                                <span
                                  className={`mono text-right text-[13px] font-light tabular ${
                                    t.amount > 0 ? "text-[#00FF87]" : "text-white/85"
                                  }`}
                                >
                                  {formatSigned(t.amount)}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )))}
                    </div>
                  </>
                )}
              </div>

              {/* ASIDE: riepilogo + giroconti esclusi */}
              <aside className="space-y-4">
                <div className="rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4" data-testid="transactions-summary">
                  <p className="caps-tag text-white/40">Riepilogo</p>
                  <div className="mt-3 space-y-2.5 text-[13px] font-light">
                    <div className="flex items-center justify-between">
                      <span className="text-white/45">Movimenti</span>
                      <span className="mono tabular text-white/85">{totals.count}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/45">Entrate</span>
                      <span className="mono tabular text-[#00FF87]">{formatSigned(totals.income)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/45">Uscite</span>
                      <span className="mono tabular text-white/85">{formatSigned(totals.expense)}</span>
                    </div>
                  </div>
                </div>

                <ExcludedSection compact />
              </aside>
            </div>
          </div>
        </div>
      ) : (
        /* ===================== MOBILE (invariato) ===================== */
        <div className="min-h-dvh pb-10">
          <div className="sticky top-0 z-20 border-b border-white/[0.04] bg-[rgba(5,5,8,0.85)] backdrop-blur-[20px]">
            <div className="flex items-center justify-between px-3 py-3">
              <button
                onClick={() => navigate("/")}
                data-testid="transactions-back-button"
                aria-label="Torna alla home"
                className="flex h-9 w-9 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/60 hover:bg-white/[0.05]"
              >
                <ArrowLeft size={16} />
              </button>
              <h1 className="caps-tag !text-[11px] text-white/80">Transazioni</h1>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCashOpen(true)}
                  data-testid="transactions-add-cash-button"
                  aria-label="Aggiungi movimento in contanti"
                  className="flex h-9 items-center gap-1.5 rounded-[6px] border border-[#00FF87]/35 bg-transparent px-2.5 text-[10px] font-medium uppercase tracking-[0.16em] text-[#00FF87] hover:bg-[#00FF87]/[0.06] active:scale-[0.97]"
                >
                  <Banknote size={12} />
                  Contanti
                </button>
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="transactions-import-csv-button"
                  aria-label="Importa CSV Revolut"
                  className="flex h-9 items-center gap-2 rounded-[6px] border border-white/[0.12] bg-transparent px-3 text-[10px] font-medium uppercase tracking-[0.18em] text-white/75 hover:bg-white/[0.05]"
                >
                  <Upload size={12} />
                  CSV
                </button>
              </div>
            </div>
          </div>

          <div className="px-4">
            <div className="pt-4">
              <ScopeFilter />
            </div>
            {txs === null ? (
              <div className="space-y-3 pt-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <div className="flex-1">
                      <div className="h-3.5 w-2/3 animate-pulse bg-white/[0.06]" />
                      <div className="mt-1.5 h-2.5 w-1/3 animate-pulse bg-white/[0.04]" />
                    </div>
                    <div className="h-3.5 w-16 animate-pulse bg-white/[0.06]" />
                  </div>
                ))}
              </div>
            ) : txs.length === 0 ? (
              <p className="mono pt-20 text-center text-sm font-light text-white/40" data-testid="transactions-empty">
                ❯ Nessuna transazione. Importa un CSV Revolut.
              </p>
            ) : groups.length === 0 ? (
              <p className="mono pt-16 text-center text-sm font-light text-white/40" data-testid="scope-filter-empty">
                ❯ Nessun movimento {scopeFilter === "business" ? "business" : "personale"} in questo elenco.
              </p>
            ) : (
              groups.map(([label, items]) => (
                <div key={label}>
                  <p className="caps-tag pb-1.5 pt-5 text-white/35">{label}</p>
                  <div className="overflow-hidden rounded-[12px] border border-white/[0.06] bg-[#0D0D14]">
                    {items.map((t, i) => (
                      <motion.button
                        key={t.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.15, delay: Math.min(i * 0.015, 0.2) }}
                        onClick={() => setSelectedTx(t)}
                        data-testid="transaction-row"
                        className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.03] active:bg-white/[0.05] ${
                          i > 0 ? "border-t border-white/[0.04]" : ""
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="truncate text-[13px] font-light text-white/85">{t.description}</p>
                            {t.source === "cash" && (
                              <span
                                className="caps-tag shrink-0 rounded-[4px] border border-[#00FF87]/35 px-1 py-0.5 text-[#00FF87]"
                                style={{ fontSize: "8px" }}
                                data-testid="cash-badge"
                              >
                                Contanti
                              </span>
                            )}
                            {t.scope === "business" && (
                              <span
                                className="caps-tag shrink-0 rounded-[4px] border border-[#F5A623]/40 px-1 py-0.5 text-[#F5A623]"
                                style={{ fontSize: "8px" }}
                                data-testid="business-badge"
                              >
                                Business
                              </span>
                            )}
                          </div>
                          <p className="caps-tag mt-1" style={{ fontSize: "9px" }}>
                            <span className="mono text-white/30">{formatTime(t.date)}</span>
                            <span className="mx-1.5 text-white/15">·</span>
                            <span style={{ color: categoryColor(t.category) }}>{t.category || "Da classificare"}</span>
                          </p>
                        </div>
                        <span
                          className={`mono shrink-0 text-[13px] font-light tabular ${t.amount > 0 ? "text-[#00FF87]" : "text-white/85"}`}
                        >
                          {formatSigned(t.amount)}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              ))
            )}

            <ExcludedSection />
          </div>
        </div>
      )}

      {/* input file nascosto (condiviso tra mobile e desktop) */}
      <input
        ref={fileRef}
        type="file"
        accept=".csv"
        onChange={handleFile}
        className="hidden"
        data-testid="csv-upload-input"
      />

      <TransactionDetailModal
        tx={selectedTx}
        categories={categories}
        onClose={() => setSelectedTx(null)}
        onUpdated={(updated) => setTxs((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))}
        onTransferred={handleTransferred}
        onDeleted={(id) => {
          setSelectedTx(null);
          setTxs((prev) => prev.filter((t) => t.id !== id));
        }}
      />

      <CashEntryModal
        open={cashOpen}
        onClose={() => setCashOpen(false)}
        categories={categories}
        onAdded={() => load().catch(() => toast.error("Errore nel ricaricamento"))}
      />
    </div>
  );
}
